import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import InlineAlert from '../../components/InlineAlert';
import { useBilling } from '../../context/useBilling';

const testCardProfiles = [
  { label: 'Visa (Default)', number: '4111 1111 1111 1111', expiry: '12/29', cvv: '123', zip: '28205' },
  { label: 'Mastercard', number: '5555 5555 5555 4444', expiry: '06/28', cvv: '321', zip: '30301' },
  { label: 'Amex', number: '3782 822463 10005', expiry: '09/27', cvv: '1234', zip: '10001' }
];

const formatCardNumber = (raw) => {
  const digits = raw.replace(/\D/g, '');
  if (/^3[47]/.test(digits)) {
    return digits.replace(/(\d{4})(\d{0,6})(\d{0,5}).*/, (m, g1, g2, g3) => [g1, g2, g3].filter(Boolean).join(' ')).trim();
  }
  return digits.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
};

const formatExpiry = (raw) => {
  const digits = raw.replace(/\D/g, '').slice(0, 4);
  if (digits.length <= 2) return digits;
  return digits.slice(0, 2) + '/' + digits.slice(2);
};

const CollectPayment = () => {
  const { account, currentBill, processPayment, paymentResult, loading, error, resetPaymentResult } = useBilling();
  const initialAmount = currentBill ? currentBill.totals.amountDue.toFixed(2) : '0.00';
  const [paymentForm, setPaymentForm] = useState({
    amount: initialAmount, method: 'card', cardNumber: '', expiry: '', cvv: '', billingZip: ''
  });
  const [paymentErrors, setPaymentErrors] = useState({});
  const submittingPayment = loading.payment;
  const paymentSuccess = !!paymentResult;

  useEffect(() => {
    if (currentBill) {
      setPaymentForm(p => ({ ...p, amount: currentBill.totals.amountDue.toFixed(2) }));
    }
  }, [currentBill]);

  const idToKey = (id) => {
    switch (id) {
      case 'payment-amount': return 'amount';
      case 'payment-method': return 'method';
      case 'card-number': return 'cardNumber';
      case 'card-expiry': return 'expiry';
      case 'card-cvv': return 'cvv';
      case 'billing-zip': return 'billingZip';
      default: return id;
    }
  };

  const handlePaymentChange = (e) => {
    const { id, value } = e.target;
    let next = value;
    if (id === 'card-number') next = formatCardNumber(value);
    if (id === 'card-expiry') next = formatExpiry(value);
    setPaymentForm(prev => ({ ...prev, [idToKey(id)]: next }));
    setPaymentErrors(prev => ({ ...prev, [idToKey(id)]: undefined }));
  };

  const validatePayment = () => {
    const errs = {};
    if (!paymentForm.amount || isNaN(Number(paymentForm.amount)) || Number(paymentForm.amount) <= 0) {
      errs.amount = 'Enter a valid amount > 0';
    }
    if (paymentForm.method === 'card') {
      const digits = paymentForm.cardNumber.replace(/\s+/g, '');
      if (digits.length < 13 || digits.length > 19) errs.cardNumber = 'Card number length invalid';
      if (!/^(0[1-9]|1[0-2])\/(\d{2})$/.test(paymentForm.expiry)) errs.expiry = 'Use MM/YY';
      if (!/^\d{3,4}$/.test(paymentForm.cvv)) errs.cvv = '3 or 4 digit CVV';
      if (!/^\d{5}$/.test(paymentForm.billingZip)) errs.billingZip = '5 digit ZIP';
    }
    setPaymentErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmitPayment = (e) => {
    e.preventDefault();
    if (!validatePayment()) return;
    processPayment({
      amount: paymentForm.amount,
      method: paymentForm.method,
      cardNumber: paymentForm.cardNumber
    });
  };

  const resetPayment = () => {
    setPaymentForm({ amount: initialAmount, method: 'card', cardNumber: '', expiry: '', cvv: '', billingZip: '' });
    setPaymentErrors({});
    resetPaymentResult();
  };

  const applyTestCard = (idx) => {
    const profile = testCardProfiles[idx];
    if (!profile) return;
    setPaymentForm(prev => ({
      ...prev,
      method: 'card',
      cardNumber: formatCardNumber(profile.number),
      expiry: profile.expiry,
      cvv: profile.cvv,
      billingZip: profile.zip
    }));
    setPaymentErrors({});
    resetPaymentResult();
  };

  const billPeriod = currentBill
    ? `${new Date(currentBill.period.start).toLocaleDateString()} – ${new Date(currentBill.period.end).toLocaleDateString()}`
    : '—';

  return (
    <div className="row billing-panel" role="tabpanel" id="billing-panel-collect-payment" aria-labelledby="billing-tab-collect-payment" tabIndex={0} data-testid="billing-panel-collect-payment">
      <div className="col-lg-7 mb-4">
        <div className="card border-0 shadow-sm h-100 billing-surface-card billing-surface-card--form">
          <div className="card-header bg-light border-0">
            <h5 className="card-title mb-0 d-flex align-items-center">
              <i className="bi bi-credit-card me-2 text-tse-primary"></i>
              Collect Payment
            </h5>
          </div>
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-start mb-3">
              <InlineAlert
                variant="info"
                icon="info-circle"
                message="Select payment method and enter required details to submit a payment."
                className="small py-2 px-3 mb-0"
              />
              <div className="d-flex align-items-center gap-2">
                <label htmlFor="test-card-profile" className="form-label mb-0 small text-muted">Test Card</label>
                <select
                  id="test-card-profile"
                  className="form-select form-select-sm"
                  onChange={(e) => { if (e.target.value) applyTestCard(Number(e.target.value)); }}
                  defaultValue=""
                  aria-label="Select Test Card Profile"
                  style={{ width: '180px' }}
                >
                  <option value="" disabled>Choose…</option>
                  {testCardProfiles.map((p, i) => (
                    <option key={p.label} value={i}>{p.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <form id="collect-payment-form" aria-label="Collect Payment Form" onSubmit={handleSubmitPayment} noValidate data-testid="form-collect-payment">
              <fieldset className="border-0 p-0 m-0" aria-describedby="payment-instructions">
                <legend className="visually-hidden">Enter payment amount and method</legend>
                <div id="payment-instructions" className="visually-hidden">Provide the full amount due and card details. All fields are required.</div>
              <div className="mb-3">
                <label htmlFor="payment-amount" className="form-label">Payment Amount</label>
                <div className="input-group has-validation">
                  <span className="input-group-text">$</span>
                  <input
                    type="text"
                    id="payment-amount"
                    className={`form-control ${paymentErrors.amount ? 'is-invalid' : ''}`}
                    placeholder="175.58"
                    value={paymentForm.amount}
                    onChange={handlePaymentChange}
                    aria-describedby={paymentErrors.amount ? 'payment-amount-error' : undefined}
                    inputMode="decimal"
                    data-testid="input-payment-amount"
                    aria-invalid={paymentErrors.amount ? 'true' : 'false'}
                  />
                  {paymentErrors.amount && <div id="payment-amount-error" className="invalid-feedback">{paymentErrors.amount}</div>}
                </div>
              </div>
              <div className="mb-3">
                <label htmlFor="payment-method" className="form-label">Payment Method</label>
                <select
                  id="payment-method"
                  className="form-select"
                  value={paymentForm.method}
                  onChange={handlePaymentChange}
                  data-testid="select-payment-method"
                >
                  <option value="card">Credit / Debit Card</option>
                  <option value="ach" disabled>Bank Draft (ACH) - Coming Soon</option>
                </select>
              </div>
              {paymentForm.method === 'card' && (
                <div className="row g-3 mb-3">
                  <div className="col-12">
                    <label htmlFor="card-number" className="form-label">Card Number</label>
                    <input
                      id="card-number"
                      type="text"
                      className={`form-control ${paymentErrors.cardNumber ? 'is-invalid' : ''}`}
                      placeholder="4111 1111 1111 1111"
                      value={paymentForm.cardNumber}
                      onChange={handlePaymentChange}
                      aria-describedby={paymentErrors.cardNumber ? 'card-number-error' : undefined}
                      inputMode="numeric"
                      autoComplete="cc-number"
                      data-testid="input-card-number"
                      aria-invalid={paymentErrors.cardNumber ? 'true' : 'false'}
                    />
                    {paymentErrors.cardNumber && <div id="card-number-error" className="invalid-feedback">{paymentErrors.cardNumber}</div>}
                  </div>
                  <div className="col-md-4">
                    <label htmlFor="card-expiry" className="form-label">Expiry</label>
                    <input
                      id="card-expiry"
                      type="text"
                      className={`form-control ${paymentErrors.expiry ? 'is-invalid' : ''}`}
                      placeholder="MM/YY"
                      value={paymentForm.expiry}
                      onChange={handlePaymentChange}
                      aria-describedby={paymentErrors.expiry ? 'card-expiry-error' : undefined}
                      inputMode="numeric"
                      autoComplete="cc-exp"
                      data-testid="input-card-expiry"
                      aria-invalid={paymentErrors.expiry ? 'true' : 'false'}
                    />
                    {paymentErrors.expiry && <div id="card-expiry-error" className="invalid-feedback">{paymentErrors.expiry}</div>}
                  </div>
                  <div className="col-md-4">
                    <label htmlFor="card-cvv" className="form-label">CVV</label>
                    <input
                      id="card-cvv"
                      type="password"
                      className={`form-control ${paymentErrors.cvv ? 'is-invalid' : ''}`}
                      placeholder="123"
                      value={paymentForm.cvv}
                      onChange={handlePaymentChange}
                      aria-describedby={paymentErrors.cvv ? 'card-cvv-error' : undefined}
                      inputMode="numeric"
                      autoComplete="cc-csc"
                      data-testid="input-card-cvv"
                      aria-invalid={paymentErrors.cvv ? 'true' : 'false'}
                    />
                    {paymentErrors.cvv && <div id="card-cvv-error" className="invalid-feedback">{paymentErrors.cvv}</div>}
                  </div>
                  <div className="col-md-4">
                    <label htmlFor="billing-zip" className="form-label">Billing ZIP</label>
                    <input
                      id="billing-zip"
                      type="text"
                      className={`form-control ${paymentErrors.billingZip ? 'is-invalid' : ''}`}
                      placeholder="28205"
                      value={paymentForm.billingZip}
                      onChange={handlePaymentChange}
                      aria-describedby={paymentErrors.billingZip ? 'billing-zip-error' : undefined}
                      inputMode="numeric"
                      autoComplete="postal-code"
                      data-testid="input-billing-zip"
                      aria-invalid={paymentErrors.billingZip ? 'true' : 'false'}
                    />
                    {paymentErrors.billingZip && <div id="billing-zip-error" className="invalid-feedback">{paymentErrors.billingZip}</div>}
                  </div>
                </div>
              )}
              {paymentSuccess && paymentResult && (
                <InlineAlert
                  variant="success"
                  icon="check-circle"
                  message="Payment recorded. Confirmation"
                  live="assertive"
                  dataTestId="alert-payment-success"
                >
                  {' '}
                  <Link id="cp-confirmation-code-link" to="../billing-history" className="alert-link fw-semibold" data-testid="link-confirmation-to-history">
                    <span id="cp-confirmation-code">{paymentResult.confirmationCode}</span> <i className="bi bi-arrow-right-circle ms-1"></i>
                  </Link>
                </InlineAlert>
              )}
              {!paymentSuccess && error && !loading.payment && (
                <InlineAlert
                  variant="danger"
                  icon="exclamation-triangle"
                  ariaLabel="Payment processing error"
                  message={error}
                  live="assertive"
                  dataTestId="alert-payment-error"
                />
              )}
              <div className="d-flex gap-2">
                <button type="submit" className="btn btn-tse-primary" disabled={submittingPayment || paymentSuccess || Number(paymentForm.amount) <= 0} data-testid="btn-submit-payment">
                  {submittingPayment ? <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Processing...</> : <><i className="bi bi-lock me-2"></i>Submit Payment</>}
                </button>
                <button type="button" className="btn btn-outline-secondary" onClick={resetPayment} disabled={submittingPayment} data-testid="btn-reset-payment">
                  Reset
                </button>
              </div>
              </fieldset>
            </form>
          </div>
        </div>
      </div>
      <div className="col-lg-5 mb-4">
        <div className="card border-0 shadow-sm h-100 billing-surface-card billing-surface-card--aside">
          <div className="card-header bg-light border-0">
            <h6 className="mb-0 d-flex align-items-center">
              <i className="bi bi-receipt me-2 text-secondary"></i>Bill & Account Summary
            </h6>
          </div>
          <div className="card-body small">
            <div className="mb-3">
              <div className="d-flex justify-content-between">
                <span id="cp-lbl-account-number" className="text-muted">Account #</span>
                <span id="cp-val-account-number" className="fw-semibold billing-mono" aria-labelledby="cp-lbl-account-number">
                  {account?.accountNumber ?? '—'}
                </span>
              </div>
              <div className="d-flex justify-content-between">
                <span id="cp-lbl-bill-period" className="text-muted">Bill Period</span>
                <span id="cp-val-bill-period" aria-labelledby="cp-lbl-bill-period">{billPeriod}</span>
              </div>
              <div className="d-flex justify-content-between">
                <span id="cp-lbl-amount-due" className="text-muted">Amount Due</span>
                <span id="cp-val-amount-due" className="fw-semibold" aria-labelledby="cp-lbl-amount-due">
                  {currentBill ? `$${currentBill.totals.amountDue.toFixed(2)}` : '—'}
                </span>
              </div>
              <div className="d-flex justify-content-between">
                <span id="cp-lbl-due-date" className="text-muted">Due Date</span>
                <span id="cp-val-due-date" className="text-danger fw-semibold" aria-labelledby="cp-lbl-due-date">
                  {currentBill ? new Date(currentBill.period.dueDate).toLocaleDateString() : '—'}
                </span>
              </div>
            </div>
            <hr />
            <div className="mb-3">
              <h6 className="fw-bold mb-2">Security Notes</h6>
              <ul className="small ps-3 mb-0">
                <li>Card data is not stored.</li>
                <li>All transactions are encrypted.</li>
                <li>Submit only with caller authorization.</li>
              </ul>
            </div>
            <InlineAlert
              variant="warning"
              icon="shield-lock"
              message="Sandbox mode: no live charges."
              className="py-2 px-3 small mb-0"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CollectPayment;
